import { useMemo, useRef, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getApiVersion } from '@/api/version'
import {
  LayoutDashboard, BarChart3, Globe, Server,
  ChevronLeft, ChevronRight as Expand, ChevronRight, ChevronDown, User2, LogOut,
  History,
  // Icon map — all icons the backend catalogue may reference
  MapPin, Radio, Clock, Route, Terminal,
  Bell, SlidersHorizontal, Tag,
  Truck, List, Cpu, Layers, WalletCards, RefreshCw,
  Globe2, CreditCard, BarChart2, FileText,
  Wrench, Package, Zap, Share2, Settings,
  Users, UserCheck, ShieldCheck, KeyRound,
  Briefcase, CheckSquare, Calendar, Send,
  DollarSign, BookOpen, Map, AlertCircle,
  Heart, Battery, Link2, Repeat, Grid3X3,
  Building2, Receipt, Wallet, Car, School,
  PawPrint, Bus, Pentagon, Video,
  // Additional icons from backend menu catalogue
  Droplet, PieChart, Activity, UploadCloud,
  Mail, Download, Pencil, Award,
} from 'lucide-react'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { useTenantStore } from '@/store/tenant'
import { getMenu, type MenuItem } from '@/api/menu'
import type { PermissionMap } from '@/types'
import { cn } from '@/utils/cn'

// ── Icon resolver ──────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  'history': History,
  'map-pin': MapPin,
  'radio': Radio,
  'clock': Clock,
  'route': Route,
  'terminal': Terminal,
  'server': Server,
  'bell': Bell,
  'sliders': SlidersHorizontal,
  'tag': Tag,
  'pentagon': Pentagon,
  'truck': Truck,
  'list': List,
  'cpu': Cpu,
  'layers': Layers,
  'sim-card': WalletCards,
  'refresh': RefreshCw,
  'globe': Globe,
  'globe2': Globe2,
  'credit-card': CreditCard,
  'bar-chart': BarChart3,
  'bar-chart-2': BarChart2,
  'file-text': FileText,
  'tool': Wrench,
  'package': Package,
  'zap': Zap,
  'share-2': Share2,
  'settings': Settings,
  'users': Users,
  'user-check': UserCheck,
  'shield': ShieldCheck,
  'shield-check': ShieldCheck,
  'key': KeyRound,
  'briefcase': Briefcase,
  'check-square': CheckSquare,
  'calendar': Calendar,
  'send': Send,
  'dollar-sign': DollarSign,
  'book': BookOpen,
  'map': Map,
  'bus': Bus,
  'alert-circle': AlertCircle,
  'heart': Heart,
  'battery': Battery,
  'link': Link2,
  'repeat': Repeat,
  'grid': Grid3X3,
  'building': Building2,
  'receipt': Receipt,
  'wallet': Wallet,
  'car': Car,
  'school': School,
  'paw-print': PawPrint,
  'video':       Video,
  // Additional icons
  'droplet':     Droplet,
  'pie-chart':   PieChart,
  'activity':    Activity,
  'upload-cloud': UploadCloud,
  'box':         Package,
  'mail':        Mail,
  'download':    Download,
  'edit-2':      Pencil,
  'layout':      FileText,
  'award':       Award,
  'trophy':      Award,
  'star':        Award,
}

function resolveIcon(name: string): React.ElementType {
  return ICON_MAP[name] ?? Settings
}

// ── Route map: backend menu key → frontend route ───────────────────────────────

const ROUTE_MAP: Record<string, string> = {
  // Tracking
  'tracking.live':     '/map',
  'tracking.history':  '/history',
  'tracking.trips':    '/trips',
  'tracking.packets':  '/packets',
  // Alerts
  'alerts.list':       '/alerts',
  'alerts.manage':     '/alerts',
  'alerts.types':      '/admin/alert-types',
  'alerts.rules':      '/admin/alert-rules',
  'admin.alert_rules': '/admin/alert-rules',
  // Geofences
  'geofences.list':            '/geofences',
  'geofences.events':          '/geofences',
  'geofences.assign_vehicles': '/geofences',
  // Vehicles
  'vehicles.list':          '/vehicles',
  'vehicles.assign_device': '/vehicles',
  'vehicles.types':         '/admin/vehicle-types',
  // Drivers
  'drivers.list':           '/drivers',
  'drivers.assign_vehicle': '/drivers',
  // Devices
  'devices.list':  '/devices',
  'devices.types': '/devices',
  // SIMs
  'sims.list':      '/sims',
  'sims.sync':      '/sims',
  'sims.providers': '/sims',
  // Billing
  'billing.dashboard':           '/billing/dashboard',
  'billing.platform_dashboard':  '/billing/dashboard',
  'billing.overview':            '/billing/subscriptions',
  'billing.profiles':            '/billing/profile',
  'billing.profile':             '/billing/profile',
  'billing.manage_profiles':     '/billing/profile',
  // Customer's own subscription view (distinct route — avoids deduplication with overview)
  'billing.subscription':        '/billing/my-subscription',
  'billing.subscriptions':       '/billing/my-subscription',
  'billing.manage_subscription': '/billing/my-subscription',
  'billing.entities':            '/billing/my-subscription',
  'billing.manage_entities':     '/billing/my-subscription',
  'billing.invoices':            '/billing/invoices',
  'billing.manage_invoices':     '/billing/invoices',
  'billing.payments':            '/billing/my-billing',
  'billing.manage_payments':     '/billing/my-billing',
  'billing.wallet':              '/billing/my-billing',
  'billing.manage_wallet':       '/billing/my-billing',
  'billing.my_billing':          '/billing/my-billing',
  'billing.manage_customers':    '/billing/customers',
  'billing.plans':               '/billing/domain-plans',
  'billing.domain_plans':        '/billing/domain-plans',
  'billing.platform_plans':      '/billing/plans',
  'billing.manage_domain_plans': '/billing/domain-plans',
  'billing.platform_plan':      '/billing/platform-plan',
  'billing.gateways':            '/billing/gateways',
  'billing.manage_gateways':     '/billing/gateways',
  // Technician
  'technician.stock':    '/admin/notifications',
  'technician.activate': '/admin/notifications',
  'technician.handover': '/admin/notifications',
  // Field Force
  'fieldforce.list':       '/field-force/employees',
  'fieldforce.tasks':      '/field-force/tasks',
  'fieldforce.attendance': '/field-force/attendance',
  // Dispatch
  'dispatch.list':     '/dispatch/bookings',
  'dispatch.services': '/dispatch/service-types',
  'dispatch.finance':  '/dispatch/bookings',
  'dispatch.packages': '/dispatch/packages',
  // School
  'school.list':       '/school/schools',
  'school.students':   '/school/students',
  'school.attendance': '/school/attendance',
  'school.routes':     '/school/routes',
  // Transport
  'transport.list':      '/transport/routes',
  'transport.roster':    '/transport/rosters',
  'transport.sos':       '/transport/sos',
  'transport.vendors':   '/transport/vendors',
  'transport.employees': '/transport/employees',
  // Pets
  'pets.list':   '/pets',
  'pets.breeds': '/pets',
  // Batteries / EV
  'batteries.list':   '/ev/batteries',
  'batteries.assign': '/ev/batteries',
  'batteries.swap':   '/ev/swaps',
  // EV Stations
  'ev_stations.manage': '/ev/stations',
  // Reports (many possible backend keys)
  'reports.list':           '/reports',
  'reports.view':           '/reports',
  'reports.all':            '/reports',
  'reports.generate':       '/reports',
  'reports.trips':          '/reports',
  'reports.trip':           '/reports',
  'reports.speed':          '/reports',
  'reports.distance':       '/reports',
  'reports.mileage':        '/reports',
  'reports.ignition':       '/reports',
  'reports.geofence':       '/reports',
  'reports.geofence_events': '/reports',
  'reports.alerts':         '/reports',
  'reports.alert':          '/reports',
  'reports.summary':        '/reports',
  'reports.manage':         '/reports',

  // Scoring
  'scoring.leaderboard': '/scoring',
  'scoring.config':      '/scoring',
  'scoring.view':        '/scoring',
  'scoring.list':        '/scoring',

  // System (super admin)
  'system.services': '/system',
  'system.parsers':  '/system/parsers',

  // Admin
  'admin.users':             '/admin/users',
  'admin.manage_users':      '/admin/users',
  'admin.user_list':         '/admin/users',
  'admin.list_users':        '/admin/users',
  'admin.user_types':        '/admin/user-types',
  'admin.permissions':       '/admin/user-types',
  'admin.manage_user_types': '/admin/user-types',
  'admin.data_brokers':      '/admin/data-brokers',
  'admin.api_keys':          '/admin/api-keys',
  'admin.dashcam_config':    '/admin/dashcam-config',
  'admin.doc_reminders':     '/admin/document-reminders',
  // Alert Rules — extra key formats the backend may send
  'alert_rules':            '/admin/alert-rules',
  'alert_rule':             '/admin/alert-rules',
  // Calendars
  'calendars':              '/admin/calendars',
  'admin.calendars':        '/admin/calendars',
  'calendar':               '/admin/calendars',
  // Billing extras
  'billing.customers':           '/billing/customers',
  'billing.pdf_templates':       '/billing/pdf-templates',
  'billing.pdf-templates':       '/billing/pdf-templates',
  'billing.tax_config':          '/billing/tax-config',
  'billing.signing_cert':        '/billing/signing-cert',
  'billing.dunning_rules':       '/billing/dunning-rules',
  'billing.platform_profile':        '/billing/platform-profile',
  'billing.platform_gateways':       '/billing/platform-gateways',
  'billing.platform_pdf_templates':  '/billing/platform-pdf-templates',
  // Billing statement & exports — backend menu keys from /menu API
  'billing.statement':           '/billing/my-billing',
  'billing.exports':             '/billing/tax-config',
  // Integration & settings keys from /menu API
  'integrations.databroker':     '/admin/data-brokers',
  'settings.domain':             '/admin/domain',
  'settings.notifications':      '/admin/notifications',
  'calendars.list':              '/admin/calendars',
  'calendars.manage':            '/admin/calendars',
}

// ── Label overrides — force specific display labels regardless of API value ────
const LABEL_OVERRIDE: Record<string, string> = {
  'billing.dashboard':           'Billing Dashboard',
  'billing.platform_dashboard':  'Billing Dashboard',
  'billing.subscription':        'My Subscription',
  'billing.manage_subscription': 'My Subscription',
  'billing.entities':            'My Subscription',
  'billing.manage_entities':     'My Subscription',
  'billing.payments':            'My Billing',
  'billing.manage_payments':     'My Billing',
  'billing.wallet':              'My Billing',
  'billing.manage_wallet':       'My Billing',
  'billing.statement':           'My Billing',
  'billing.my_billing':          'My Billing',
  'tracking.live':         'Live',
  'tracking.history':      'GPS History',
  'tracking.trips':        'Trips',
  'alert_rules':           'Alert Rules',
  'alert_rule':            'Alert Rules',
  'billing.invoices':          'Domain / My Billing',
  'billing.manage_invoices':   'Domain / My Billing',
  'billing.customers':         'Customer Billing',
  'billing.manage_customers':  'Customer Billing',
  'billing.plans':             'Plans',
  'billing.domain_plans':      'Plans',
  'billing.platform_plans': 'Platform Plans',
  // All reports.* keys that link to /reports → rename to "On-Road Summary"
  'reports.list':     'On-Road Summary',
  'reports.view':     'On-Road Summary',
  'reports.all':      'On-Road Summary',
  'reports.generate': 'On-Road Summary',
  'reports.trips':    'On-Road Summary',
  'reports.trip':     'On-Road Summary',
  'reports.summary':  'On-Road Summary',
  'reports.manage':   'On-Road Summary',
  'reports.mileage':  'On-Road Summary',
  'reports.distance': 'On-Road Summary',
  'reports.speed':    'On-Road Summary',
  'reports.ignition': 'On-Road Summary',
  'reports.geofence': 'On-Road Summary',
  'reports.alerts':   'On-Road Summary',
  'reports.alert':    'On-Road Summary',
}

// Keys that must only be visible to super-admin (platform-level features)
const SUPER_ADMIN_ONLY_KEYS = new Set([
  'billing.platform_plans',
  'billing.platform_profile',
  'billing.platform_gateways',
  // System health / infra — super admin only
  'system.services',
  'system.parsers',
])

// Keys that are only meaningful for domain admins (not for owner/customer accounts)
const ADMIN_ONLY_KEYS = new Set([
  'billing.customers',  // customer billing management
])

// Keys only for end-customers/owners — hidden from domain admins AND super admins
const CUSTOMER_ONLY_KEYS = new Set([
  'billing.subscription',
  'billing.subscriptions',
  'billing.manage_subscription',
  'billing.entities',
  'billing.manage_entities',
])

// Keys that must NOT be visible to super-admin (domain-specific features)
const SUPER_ADMIN_HIDDEN_KEYS = new Set([
  'billing.plans',
  'billing.domain_plans',
  'billing.manage_domain_plans',
  'billing.customer_plan',
  'billing.customer_plans',
  'billing.manage_customer_plan',
  'billing.manage_customer_plans',
  'billing.customers',       // "Customer Plans" label from API — domain-admin feature
  'billing.gateways',        // domain-scoped — super admin uses billing.platform_gateways instead
  'billing.manage_gateways', // same as above
  'billing.pdf_templates',   // domain-scoped — super admin uses billing.platform_pdf_templates instead
  'billing.pdf-templates',   // same as above
])

// Keys permanently hidden from the sidebar for all users
const ALWAYS_HIDDEN_KEYS = new Set([
  'billing.overview',   // domain subscription page — removed from nav
])

// ── Post-process menu from /menu API ──────────────────────────────────────────
// 1. Apply label overrides
// 2. Move tracking.history + tracking.trips out of Tracking → into Reports
// 3. Strip super-admin-only items for non-super users
// 4. Strip admin-only items for owner/customer accounts
function postProcessMenu(groups: MenuItem[], isSuperAdmin: boolean, isAdmin: boolean): MenuItem[] {
  const MOVE_TO_REPORTS = new Set(['tracking.history', 'tracking.trips'])

  // First pass: collect items to promote to Reports
  const promotedItems: MenuItem[] = []
  groups.forEach((g) => {
    if (g.key === 'tracking') {
      ;(g.children ?? []).forEach((c) => {
        if (MOVE_TO_REPORTS.has(c.key)) promotedItems.push(c)
      })
    }
  })

  return groups.map((group) => {
    const applyOverride = (c: MenuItem): MenuItem => ({
      ...c,
      label: LABEL_OVERRIDE[c.key] ?? c.label,
    })

    const filterHidden           = (c: MenuItem) => !ALWAYS_HIDDEN_KEYS.has(c.key)
    const filterSuperAdminOnly   = (c: MenuItem) => isSuperAdmin || !SUPER_ADMIN_ONLY_KEYS.has(c.key)
    const filterSuperAdminHidden = (c: MenuItem) => !isSuperAdmin || !SUPER_ADMIN_HIDDEN_KEYS.has(c.key)
    const filterAdminOnly        = (c: MenuItem) => isAdmin      || !ADMIN_ONLY_KEYS.has(c.key)
    // Customer-only pages: hide from both super admin and domain admin
    const isAdminOrSuper = isSuperAdmin || isAdmin
    const filterCustomerOnly     = (c: MenuItem) => !isAdminOrSuper || !CUSTOMER_ONLY_KEYS.has(c.key)

    if (group.key === 'tracking') {
      return {
        ...group,
        children: (group.children ?? [])
          .filter((c) => !MOVE_TO_REPORTS.has(c.key))
          .filter(filterHidden)
          .filter(filterSuperAdminOnly)
          .filter(filterSuperAdminHidden)
          .filter(filterCustomerOnly)
          .filter(filterAdminOnly)
          .map(applyOverride),
      }
    }

    if (group.key === 'reports') {
      return {
        ...group,
        children: [
          ...promotedItems.map(applyOverride),
          ...(group.children ?? []).map((c) => {
            // Any child that routes to /reports → rename to "On-Road Summary"
            const route = ROUTE_MAP[c.key]
            if (route === '/reports') return { ...c, label: 'On-Road Summary' }
            return applyOverride(c)
          }),
        ],
      }
    }

    return {
      ...group,
      children: (group.children ?? [])
        .filter(filterHidden)
        .filter(filterSuperAdminOnly)
        .filter(filterSuperAdminHidden)
        .filter(filterCustomerOnly)
        .filter(filterAdminOnly)
        .map(applyOverride),
    }
  })
}

// ── Group-level route fallback ─────────────────────────────────────────────────
// If a child key from the backend isn't in ROUTE_MAP, use the group's base route.
// This prevents entire menu groups from disappearing due to unknown child keys.
const GROUP_ROUTE_FALLBACK: Record<string, string> = {
  reports:    '/reports',
  tracking:   '/map',
  alerts:     '/alerts',
  geofences:  '/geofences',
  vehicles:   '/vehicles',
  drivers:    '/drivers',
  devices:    '/devices',
  sims:       '/sims',
  billing:      '/billing/subscriptions',
  integrations: '/admin/data-brokers',
  settings:     '/admin/domain',
  calendars:    '/admin/calendars',
  fieldforce:   '/field-force/employees',
  school:     '/school/schools',
  transport:  '/transport/routes',
  pets:       '/pets',
  batteries:  '/ev/batteries',
  dispatch:   '/dispatch/bookings',
  // admin group has no single fallback route — each item must be in ROUTE_MAP
  system:     '/system',
}

// ── Nav item component ─────────────────────────────────────────────────────────

function SidebarItem({
  to, label, icon: Icon, collapsed,
}: {
  to: string; label: string; icon: React.ElementType; collapsed: boolean
}) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors',
          isActive
            ? 'bg-primary/10 text-primary font-bold'
            : 'text-sidebar-fg font-medium hover:bg-sidebar-hover hover:text-sidebar-active-fg'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  )
}

// ── Menu group renderer ────────────────────────────────────────────────────────

interface InjectedItem { afterKey: string; to: string; label: string; icon: React.ElementType }

function MenuGroup({
  group, collapsed, inject = [], isOpen, onToggle,
}: {
  group: MenuItem
  collapsed: boolean
  inject?: InjectedItem[]
  isOpen: boolean
  onToggle: () => void
}) {
  const groupRef = useRef<HTMLDivElement>(null)

  // Scroll group into view when it opens so sub-items aren't hidden below the fold
  useEffect(() => {
    if (isOpen && groupRef.current) {
      // Small delay to let the DOM expand first
      const t = setTimeout(() => {
        groupRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }, 60)
      return () => clearTimeout(t)
    }
  }, [isOpen])

  const children = group.children ?? []
  if (children.length === 0) return null

  // De-duplicate children by route; fall back to group-level route for unknown keys
  const seen = new Set<string>()
  const unique = children.filter((child) => {
    const route = ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key]
    if (!route || seen.has(route)) return false
    seen.add(route)
    return true
  })
  if (unique.length === 0) return null

  // Build final list, inserting injected items after their anchor key
  const items: { key: string; to: string; label: string; icon: React.ElementType }[] = []
  for (const child of unique) {
    items.push({ key: child.key, to: ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key] ?? '/', label: child.label, icon: resolveIcon(child.icon) })
    for (const inj of inject) {
      if (inj.afterKey === child.key && !items.some((i) => i.to === inj.to)) {
        items.push({ key: `__inject_${inj.to}`, to: inj.to, label: inj.label, icon: inj.icon })
      }
    }
  }

  // Icon-only (collapsed sidebar) — show divider + items, no accordion header
  if (collapsed) {
    return (
      <>
        <div className="my-1.5 border-t border-border" />
        {items.map((item) => (
          <SidebarItem key={item.key} to={item.to} label={item.label} icon={item.icon} collapsed={collapsed} />
        ))}
      </>
    )
  }

  // Full sidebar — accordion group
  const GroupIcon = resolveIcon(group.icon ?? '')
  return (
    <div ref={groupRef} className="mt-1">
      <button
        onClick={onToggle}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2 transition-colors',
          isOpen
            ? 'text-sidebar-active-fg'
            : 'text-muted hover:text-sidebar-active-fg hover:bg-sidebar-hover'
        )}
      >
        <span className="flex items-center gap-2">
          <GroupIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider">{group.label}</span>
        </span>
        {isOpen
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        }
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5 pl-4">
          {items.map((item) => (
            <SidebarItem key={item.key} to={item.to} label={item.label} icon={item.icon} collapsed={collapsed} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Fallback menu builder (used when /menu returns empty — backend bug) ────────

function buildFallbackMenu(perms: PermissionMap, isSuperAdmin: boolean): MenuItem[] {
  const can = (r: string) => isSuperAdmin || perms[r]?.canRead === true
  const item = (key: string, label: string, icon: string): MenuItem => ({ key, label, icon })
  const group = (key: string, label: string, icon: string, children: MenuItem[]): MenuItem =>
    ({ key, label, icon, children })

  const groups: MenuItem[] = []

  // Tracking — only Live map stays here
  const trackingItems: MenuItem[] = []
  if (can('tracking') || can('vehicles')) trackingItems.push(item('tracking.live',    'Live',              'map-pin'))
  if (can('devices') || can('tracking'))  trackingItems.push(item('tracking.packets', 'Packet Inspector',  'terminal'))
  if (trackingItems.length) groups.push(group('tracking', 'Tracking', 'map-pin', trackingItems))

  // Alerts
  if (can('alerts'))    groups.push(group('alerts',    'Alerts',    'bell',    [item('alerts.list',    'Alerts',    'bell')]))
  // Geofences
  if (can('geofences')) groups.push(group('geofences', 'Geofences', 'pentagon', [item('geofences.list', 'Geofences', 'pentagon')]))
  // Vehicles
  if (can('vehicles'))  groups.push(group('vehicles',  'Vehicles',  'truck',   [item('vehicles.list', 'Vehicles', 'truck'), item('vehicles.types', 'Vehicle Types', 'layers')]))
  // Drivers
  if (can('drivers'))   groups.push(group('drivers',   'Drivers',   'user-check', [item('drivers.list', 'Drivers', 'user-check')]))
  // Devices
  if (can('devices'))   groups.push(group('devices',   'Devices',   'cpu',    [item('devices.list', 'Devices', 'cpu')]))
  // SIMs
  if (can('sims'))      groups.push(group('sims',      'SIM Cards', 'sim-card', [item('sims.list', 'SIM Cards', 'sim-card')]))

  // Billing
  if (can('billing') || can('billing_subscriptions') || can('billing_plans') || can('billing_invoices')) {
    const billingItems: MenuItem[] = []
    // Dashboard — first item for all billing users
    billingItems.push(item('billing.dashboard',    'Billing Dashboard', 'bar-chart-2'))
    // Domain admin features (shown to any non-super user with billing access)
    if (!isSuperAdmin) {
      billingItems.push(item('billing.customers',    'Customer Billing', 'users'))
      billingItems.push(item('billing.domain_plans', 'Domain Plans', 'layers'))
      billingItems.push(item('billing.platform_plan','Platform Plan','package'))
    }
    if (isSuperAdmin) {
      billingItems.push(item('billing.invoices',  'Invoices',           'receipt'))
    } else {
      billingItems.push(item('billing.invoices',  'Domain / My Billing', 'receipt'))
    }
    // Super admin uses platform gateways (isolated from domain gateways)
    if (isSuperAdmin) {
      billingItems.push(item('billing.platform_gateways', 'Payment Gateways', 'credit-card'))
    } else {
      billingItems.push(item('billing.gateways', 'Payment Gateways', 'credit-card'))
    }
    billingItems.push(item('billing.profiles',     'Profile',     'building'))
    groups.push(group('billing', 'Billing', 'credit-card', billingItems))
  }

  // Technician
  if (can('technician')) {
    groups.push(group('technician', 'Technician', 'tool', [
      item('technician.stock',    'Stock',    'package'),
      item('technician.activate', 'Activate', 'zap'),
      item('technician.handover', 'Handover', 'share-2'),
    ]))
  }

  // Field Force
  if (can('fieldforce')) {
    groups.push(group('fieldforce', 'Field Force', 'briefcase', [
      item('fieldforce.list',       'Employees',  'users'),
      item('fieldforce.tasks',      'Tasks',      'check-square'),
      item('fieldforce.attendance', 'Attendance', 'calendar'),
    ]))
  }

  // School
  if (can('school')) {
    groups.push(group('school', 'School Bus', 'school', [
      item('school.list',       'Schools',    'school'),
      item('school.routes',     'Routes',     'route'),
      item('school.students',   'Students',   'users'),
      item('school.attendance', 'Attendance', 'calendar'),
    ]))
  }

  // Transport
  if (can('transport')) {
    groups.push(group('transport', 'Transport', 'bus', [
      item('transport.vendors',   'Vendors',   'building'),
      item('transport.employees', 'Employees', 'users'),
      item('transport.list',      'Routes',    'route'),
      item('transport.roster',    'Rosters',   'list'),
      item('transport.sos',       'SOS',       'bell'),
    ]))
  }

  // Pets
  if (can('pets')) groups.push(group('pets', 'Pets', 'paw-print', [item('pets.list', 'Pets', 'paw-print')]))

  // EV / Batteries
  if (can('batteries') || can('ev_stations')) {
    const evItems: MenuItem[] = []
    if (can('batteries'))   { evItems.push(item('batteries.list', 'Batteries', 'battery')); evItems.push(item('batteries.swap', 'Swaps', 'repeat')) }
    if (can('ev_stations')) evItems.push(item('ev_stations.manage', 'EV Stations', 'zap'))
    groups.push(group('ev', 'EV Management', 'zap', evItems))
  }

  // Dispatch
  if (can('dispatch')) {
    groups.push(group('dispatch', 'Dispatch', 'send', [
      item('dispatch.list',     'Bookings',      'send'),
      item('dispatch.services', 'Service Types', 'settings'),
      item('dispatch.packages', 'Packages',      'package'),
    ]))
  }

  // Reports — GPS History + Trips + On-Road Summary
  const reportItems: MenuItem[] = []
  if (can('tracking') || can('vehicles'))  reportItems.push(item('tracking.history', 'GPS History',     'clock'))
  if (can('trips')    || can('tracking'))  reportItems.push(item('tracking.trips',   'Trips',           'route'))
  if (can('reports')  || can('report') || can('reporting'))
                                           reportItems.push(item('reports.list',     'On-Road Summary',  'bar-chart'))
  if (reportItems.length) groups.push(group('reports', 'Reports', 'bar-chart', reportItems))

  // Administration
  const adminItems: MenuItem[] = []
  if (can('users')              || isSuperAdmin) adminItems.push(item('admin.users',        'Users',        'users'))
  if (can('user_types')         || isSuperAdmin) adminItems.push(item('admin.user_types',   'User Types',   'shield'))
  if (can('databroker_configs') || isSuperAdmin) adminItems.push(item('admin.data_brokers', 'Data Brokers', 'share-2'))
  if (can('alert_rules') || isSuperAdmin) adminItems.push(item('admin.alert_rules', 'Alert Rules', 'bell'))
  if (can('calendars')   || isSuperAdmin) adminItems.push(item('admin.calendars',   'Calendars',   'calendar'))
  if (isSuperAdmin) {
    adminItems.push(item('admin.dashcam_config',  'Dashcam Config',       'video'))
    adminItems.push(item('admin.doc_reminders',   'Doc Reminders',        'bell'))
    adminItems.push(item('admin.api_keys',         'API Keys',             'key'))
  }
  if (adminItems.length) groups.push(group('admin', 'Administration', 'settings', adminItems))

  // Billing extras — visible to domain admins with billing perm (not just super-admin)
  // Tax Config, PDF Templates, Signing Cert, and Dunning Rules are domain-scoped features
  if (isSuperAdmin || can('billing') || can('billing.tax_config') || can('billing.invoices')) {
    const billingGroup = groups.find((g) => g.key === 'billing')
    if (billingGroup) {
      billingGroup.children = billingGroup.children ?? []
      billingGroup.children.push(item('billing.tax_config',    'Tax Config',        'shield'))
      billingGroup.children.push(item('billing.pdf_templates', 'PDF Templates',     'file-text'))
      billingGroup.children.push(item('billing.signing_cert',  'Signing Cert',      'award'))
      billingGroup.children.push(item('billing.exports',       'GST Exports',       'download'))
      billingGroup.children.push(item('billing.dunning_rules', 'Payment Reminders', 'bell'))
    }
  }

  // Super-admin-only billing items
  if (isSuperAdmin) {
    const billingGroup = groups.find((g) => g.key === 'billing')
    if (billingGroup) {
      billingGroup.children = billingGroup.children ?? []
      // Insert PDF Templates after Tax Config
      const taxIdx = billingGroup.children.findIndex((c) => c.key === 'billing.tax_config')
      const pdfItem = item('billing.platform_pdf_templates', 'PDF Templates', 'file-text')
      if (taxIdx >= 0) billingGroup.children.splice(taxIdx + 1, 0, pdfItem)
      else billingGroup.children.push(pdfItem)
      billingGroup.children.push(item('billing.platform_profile', 'Platform Profile', 'building'))
    }
  }

  return groups
}

// ── Main Sidebar ───────────────────────────────────────────────────────────────

export function Sidebar() {
  const { t } = useTranslation('nav')
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const theme = useTenantStore((s) => s.theme)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const is_super = useAuthStore((s) => s.is_super)
  const is_admin = useAuthStore((s) => s.user?.is_admin ?? false)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const permMap = useAuthStore((s) => s.permissions)
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded)
  const { data: apiVersion } = useQuery({
    queryKey: ['api-version'],
    queryFn: getApiVersion,
    staleTime: Infinity,
    retry: false,
  })
  const navRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const [openGroup, setOpenGroup] = useState<string | null>(null)

  const { data: menuGroups = [], isLoading } = useQuery({
    queryKey: ['menu'],
    queryFn: getMenu,
    staleTime: 5 * 60_000,
    enabled: isAuthenticated,
  })

  // If /menu returns empty (backend returns nothing for regular users), fall back
  // to building the menu from the locally cached permission map.
  const effectiveGroups = useMemo(() => {
    let groups: MenuItem[]
    const isAdmin = is_super || is_admin
    if (menuGroups.length > 0) {
      groups = postProcessMenu(menuGroups, is_super, isAdmin)
    } else if (isLoading || !permissionsLoaded) {
      return []
    } else {
      return buildFallbackMenu(permMap, is_super)
    }

    // Ensure Alert Rules and Calendars appear in the admin group whenever the
    // user has the permission — the backend may not include them in /menu.
    const injections: Array<{ permKey: string; knownKeys: string[]; item: MenuItem }> = [
      {
        permKey: 'alert_rules',
        knownKeys: ['alert_rules', 'alert_rule', 'alerts.rules', 'admin.alert_rules'],
        item: { key: 'alert_rules', label: 'Alert Rules', icon: 'bell' },
      },
      {
        permKey: 'calendars',
        knownKeys: ['calendars', 'calendar', 'admin.calendars'],
        item: { key: 'calendars', label: 'Calendars', icon: 'calendar' },
      },
    ]

    for (const { permKey, knownKeys, item } of injections) {
      const canAccess = is_super || permMap[permKey]?.canRead === true
      if (!canAccess) continue
      const keySet = new Set(knownKeys)
      const adminGroup = groups.find((g) => g.key === 'admin')
      const alreadyPresent = adminGroup && (adminGroup.children ?? []).some((c) => keySet.has(c.key))
      if (!alreadyPresent) {
        groups = groups.map((g) => {
          if (g.key !== 'admin') return g
          return { ...g, children: [...(g.children ?? []), item] }
        })
      }
    }

    // Inject "Platform Plan" for non-super users who see domain plans (i.e. domain admins).
    // Also remove "My Subscription" (customer-only) for those same users.
    // Inject Billing Dashboard at the top of billing for any admin/super user
    const billingGrp = groups.find((g) => g.key === 'billing')
    if (billingGrp) {
      const DASH_KEYS = new Set(['billing.dashboard', 'billing.platform_dashboard'])
      if (!(billingGrp.children ?? []).some((c) => DASH_KEYS.has(c.key))) {
        groups = groups.map((g) =>
          g.key === 'billing'
            ? { ...g, children: [{ key: 'billing.dashboard', label: 'Billing Dashboard', icon: 'bar-chart-2' }, ...(g.children ?? [])] }
            : g
        )
      }
    }

    // For super admin: ensure platform_gateways item is present (API may return billing.gateways
    // which is hidden by SUPER_ADMIN_HIDDEN_KEYS, so we inject the platform variant)
    if (is_super) {
      const billingGroup = groups.find((g) => g.key === 'billing')
      if (billingGroup) {
        const hasGateway = (billingGroup.children ?? []).some(
          (c) => c.key === 'billing.platform_gateways'
        )
        if (!hasGateway) {
          groups = groups.map((g) => {
            if (g.key !== 'billing') return g
            const children = [...(g.children ?? [])]
            const newItem = { key: 'billing.platform_gateways', label: 'Payment Gateways', icon: 'credit-card' }
            // Insert after the payments/wallet item to match domain admin menu position
            const PAYMENTS_KEYS = ['billing.payments', 'billing.manage_payments', 'billing.wallet', 'billing.statement', 'billing.manage_wallet']
            const paymentsIdx = children.findIndex((c) => PAYMENTS_KEYS.includes(c.key))
            if (paymentsIdx >= 0) {
              children.splice(paymentsIdx + 1, 0, newItem)
            } else {
              children.push(newItem)
            }
            return { ...g, children }
          })
        }
      }
    }

    // For super admin: ensure platform_pdf_templates item is present
    if (is_super) {
      const billingGroup = groups.find((g) => g.key === 'billing')
      if (billingGroup) {
        const hasPdfTemplates = (billingGroup.children ?? []).some(
          (c) => c.key === 'billing.platform_pdf_templates'
        )
        if (!hasPdfTemplates) {
          groups = groups.map((g) => {
            if (g.key !== 'billing') return g
            const children = [...(g.children ?? [])]
            const newItem = { key: 'billing.platform_pdf_templates', label: 'PDF Templates', icon: 'file-text' }
            // Insert after tax_config if present, else push to end
            const taxIdx = children.findIndex((c) => c.key === 'billing.tax_config')
            children.splice(taxIdx >= 0 ? taxIdx + 1 : children.length, 0, newItem)
            return { ...g, children }
          })
        }
      }
    }

    if (!is_super) {
      const DOMAIN_PLAN_KEYS = new Set(['billing.domain_plans', 'billing.plans', 'billing.manage_domain_plans'])
      const billingGroup = groups.find((g) => g.key === 'billing')
      if (billingGroup) {
        let children = (billingGroup.children ?? []).filter(
          (c) => (ROUTE_MAP[c.key] ?? '') !== '/billing/my-subscription'
        )

        // Keys we'll manually place in a fixed order; strip them from "rest"
        const DASH_SET = new Set(['billing.dashboard', 'billing.platform_dashboard'])
        const CUST_SET = new Set(['billing.customers', 'billing.manage_customers'])
        const INV_SET  = new Set(['billing.invoices',  'billing.manage_invoices'])
        // Payments/wallet items are deliberately excluded from the domain-admin sidebar
        const PAY_SET  = new Set([
          'billing.my_billing', 'billing.payments', 'billing.manage_payments',
          'billing.wallet',     'billing.manage_wallet', 'billing.statement',
        ])
        const ALL_FIXED = new Set([...DASH_SET, ...CUST_SET, ...INV_SET, ...PAY_SET])
        const rest = children.filter((c) => !ALL_FIXED.has(c.key))

        // Fixed order: Billing Dashboard → Domain/My Billing → Customer Billing → rest
        children = [
          { key: 'billing.dashboard', label: 'Billing Dashboard',   icon: 'bar-chart-2' },
          { key: 'billing.invoices',  label: 'Domain / My Billing', icon: 'receipt'     },
          { key: 'billing.customers', label: 'Customer Billing',    icon: 'users'       },
          ...rest,
        ]

        // Inject "Platform Plan" for all non-super admins with billing access
        if (!children.some((c) => c.key === 'billing.platform_plan')) {
          const domainPlansIdx = children.findIndex((c) => DOMAIN_PLAN_KEYS.has(c.key))
          const newChild = { key: 'billing.platform_plan', label: 'Platform Plan', icon: 'package' }
          children = [...children]
          children.splice(domainPlansIdx >= 0 ? domainPlansIdx + 1 : children.length, 0, newChild)
        }
        groups = groups.map((g) => g.key === 'billing' ? { ...g, children } : g)
      }
    }

    return groups
  }, [menuGroups, isLoading, permissionsLoaded, permMap, is_super, is_admin])

  // Auto-open the group whose child route matches the current pathname
  useEffect(() => {
    if (!effectiveGroups.length) return
    for (const group of effectiveGroups) {
      const children = group.children ?? []
      const hasActiveChild = children.some((child) => {
        const route = ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key]
        return route && (pathname === route || pathname.startsWith(route + '/'))
      })
      if (hasActiveChild) {
        setOpenGroup(group.key)
        return
      }
    }
  }, [pathname, effectiveGroups])

  // Scroll active nav item into view whenever the route or menu changes
  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]')
    if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [pathname, effectiveGroups])

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-30 flex flex-col bg-sidebar border-e border-border transition-all duration-200',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo area */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
        {theme?.logoUrl ? (
          <img src={theme.logoUrl} alt={theme.appName} className="h-8 w-auto object-contain" />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary text-primary-fg text-xs font-bold">
            {(theme?.appName ?? 'T').slice(0, 2).toUpperCase()}
          </div>
        )}
        {!collapsed && (
          <span className="truncate text-sm font-semibold text-sidebar-active-fg">
            {theme?.appName ?? 'Telematics'}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {/* Dashboard — always visible */}
        <SidebarItem
          to="/"
          label={t('dashboard')}
          icon={LayoutDashboard}
          collapsed={collapsed}
        />

        {/* Dynamic menu — fully driven by /menu API + permission-based fallback */}
        {isLoading ? (
          !collapsed && (
            <div className="mt-4 space-y-2 px-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton h-7 rounded" />
              ))}
            </div>
          )
        ) : (
          effectiveGroups.map((group) => (
            <MenuGroup
              key={group.key}
              group={group}
              collapsed={collapsed}
              isOpen={openGroup === group.key}
              onToggle={() => setOpenGroup(prev => prev === group.key ? null : group.key)}
            />
          ))
        )}

        {/* White Label — super admin only (not yet in /menu API) */}
        {is_super && (
          <SidebarItem
            to="/white-label/domains"
            label={t('whitelabel')}
            icon={Globe}
            collapsed={collapsed}
          />
        )}
      </nav>

      {/* User footer */}
      <div className="shrink-0 border-t border-border p-2">
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
              isActive
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-active-fg'
            )
          }
        >
          <User2 className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="flex-1 truncate">{user?.name ?? t('profile')}</span>
          )}
        </NavLink>

        <button
          onClick={() => logout()}
          className="mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-sidebar-active-fg"
          title={collapsed ? t('logout') : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <div className="flex flex-1 items-center justify-between gap-1 min-w-0">
              <span className="truncate">{t('logout')}</span>
              {apiVersion && (
                <span
                  className="shrink-0 text-[9px] text-muted/60 tabular-nums"
                  title={`Build: ${apiVersion.commit} · ${new Date(apiVersion.build_date).toLocaleDateString()}`}
                >
                  ({apiVersion.version.replace(/-unknown$/, '')}
                  {apiVersion.version.endsWith('-unknown') && <span className="text-amber-500 font-semibold"> dev</span>})
                </span>
              )}
            </div>
          )}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="absolute -end-3 top-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm hover:text-primary"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <Expand className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>
    </aside>
  )
}
